import { mediaSrc } from "@/components/ProductMediaUploader";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { http, formatINR, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState("form");
  const [order, setOrder] = useState(null);
  const [promo, setPromo] = useState("");
  const [payment, setPayment] = useState("upi");
  const [addr, setAddr] = useState({ name: user?.name || "", phone: "", line1: "", city: "", state: "", pincode: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!user || user === false) {
      toast.error("Please sign in to complete checkout");
      nav("/login?redirect=/checkout");
      return;
    }
    try {
      const { data } = await http.post("/orders/checkout", {
        items: items.map((it) => ({ product_id: it.product_id, quantity: it.quantity })),
        shipping_address: addr,
        payment_method: payment,
        promo_code: promo || undefined,
      });
      setOrder(data);
      setStep("success");
      clear();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (step === "success" && order) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="order-success">
        <div className="w-16 h-16 mx-auto rounded-full bg-[#E8F0EA] flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-[#1E3A2B]" />
        </div>
        <h1 className="mt-6 font-serif text-4xl font-bold text-[#1A201C]">Order placed with love</h1>
        <p className="mt-2 text-[#5A6B5D]">Your Ayurvedic wellness journey is on its way.</p>
        <div className="mt-8 bg-white border border-[#E2DDD5] rounded-2xl p-6 text-left">
          <div className="text-xs font-mono uppercase text-[#5A6B5D]">Order ID</div>
          <div className="font-mono text-sm">{order.id}</div>
          <div className="mt-3 text-xs font-mono uppercase text-[#5A6B5D]">AWB Tracking</div>
          <div className="font-mono text-sm">{order.awb}</div>
          <div className="mt-4 flex justify-between text-sm border-t border-[#E2DDD5] pt-4">
            <span>Total Paid</span>
            <span className="font-bold text-[#1E3A2B]">{formatINR(order.total)}</span>
          </div>
          <div className="mt-2 text-xs text-[#D97706]">You earned {order.loyalty_points_earned} Healthion loyalty points</div>
        </div>
        <button className="mt-6 btn-forest" onClick={() => nav("/account")}>View my orders</button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <p className="text-[#5A6B5D]">Your cart is empty.</p>
        <button className="mt-4 btn-forest" onClick={() => nav("/products")}>Browse products</button>
      </div>
    );
  }

  const shipping = subtotal < 1500 ? 49 : 0;
  const gst = Math.round(subtotal * 0.05);
  const total = subtotal + shipping + gst;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 grid gap-8 lg:grid-cols-[1fr_360px]">
      <form onSubmit={submit} className="bg-white border border-[#E2DDD5] rounded-2xl p-6 space-y-5" data-testid="checkout-form">
        <h1 className="font-serif text-3xl font-bold text-[#1A201C]">Checkout</h1>

        <div>
          <div className="font-serif text-xl font-semibold text-[#1E3A2B] mb-3">Shipping Address</div>
          <div className="grid grid-cols-2 gap-3">
            <input required placeholder="Full Name" value={addr.name} onChange={(e) => setAddr({ ...addr, name: e.target.value })} className="border border-[#E2DDD5] rounded-lg p-2.5 text-sm col-span-2" data-testid="addr-name" />
            <input required placeholder="Phone" value={addr.phone} onChange={(e) => setAddr({ ...addr, phone: e.target.value })} className="border border-[#E2DDD5] rounded-lg p-2.5 text-sm" data-testid="addr-phone" />
            <input required placeholder="Pincode" value={addr.pincode} onChange={(e) => setAddr({ ...addr, pincode: e.target.value })} className="border border-[#E2DDD5] rounded-lg p-2.5 text-sm" data-testid="addr-pincode" />
            <input required placeholder="Address Line" value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} className="border border-[#E2DDD5] rounded-lg p-2.5 text-sm col-span-2" data-testid="addr-line1" />
            <input required placeholder="City" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} className="border border-[#E2DDD5] rounded-lg p-2.5 text-sm" data-testid="addr-city" />
            <input required placeholder="State" value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} className="border border-[#E2DDD5] rounded-lg p-2.5 text-sm" data-testid="addr-state" />
          </div>
        </div>

        <div>
          <div className="font-serif text-xl font-semibold text-[#1E3A2B] mb-3">Payment Method</div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { v: "upi", l: "UPI" },
              { v: "card", l: "Card" },
              { v: "netbanking", l: "NetBanking" },
              { v: "cod", l: "COD" },
            ].map((p) => (
              <button
                type="button"
                key={p.v}
                onClick={() => setPayment(p.v)}
                data-testid={`payment-${p.v}`}
                className={`p-3 rounded-xl border text-sm font-semibold ${payment === p.v ? "bg-[#1E3A2B] text-white border-[#1E3A2B]" : "bg-white border-[#E2DDD5] text-[#1A201C]"}`}
              >
                {p.l}
              </button>
            ))}
          </div>
          <p className="text-[10px] font-mono text-[#5A6B5D] mt-2">MOCK payment · Razorpay/PhonePe gateway integration ready</p>
        </div>

        <div>
          <div className="font-serif text-xl font-semibold text-[#1E3A2B] mb-3">Promo Code</div>
          <input placeholder="Try HEALTHION10 or WELCOME20" value={promo} onChange={(e) => setPromo(e.target.value)} className="w-full border border-[#E2DDD5] rounded-lg p-2.5 text-sm" data-testid="promo-input" />
        </div>

        <button className="w-full btn-forest" data-testid="place-order-button">Place Order · {formatINR(total)}</button>
      </form>

      <aside className="bg-white border border-[#E2DDD5] rounded-2xl p-6 h-fit sticky top-28">
        <div className="font-serif text-xl font-semibold text-[#1E3A2B] mb-4">Order Summary</div>
        <div className="space-y-3 max-h-64 overflow-y-auto">
          {items.map((it) => (
            <div key={it.product_id} className="flex gap-3 text-sm">
              <img src={mediaSrc(it.image_url)} alt="" className="w-12 h-12 rounded-lg object-cover" />
              <div className="flex-1">
                <div className="font-semibold line-clamp-1">{it.name}</div>
                <div className="text-xs text-[#5A6B5D]">Qty {it.quantity}</div>
              </div>
              <div className="text-sm font-mono">{formatINR(it.price * it.quantity)}</div>
            </div>
          ))}
        </div>
        <div className="mt-4 border-t border-[#E2DDD5] pt-4 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-[#5A6B5D]">Subtotal</span><span className="font-mono">{formatINR(subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-[#5A6B5D]">Shipping</span><span className="font-mono">{shipping === 0 ? "Free" : formatINR(shipping)}</span></div>
          <div className="flex justify-between"><span className="text-[#5A6B5D]">GST (5%)</span><span className="font-mono">{formatINR(gst)}</span></div>
          <div className="flex justify-between font-bold text-[#1E3A2B] text-base pt-2 border-t border-[#E2DDD5]">
            <span>Total</span><span className="font-mono">{formatINR(total)}</span>
          </div>
        </div>
      </aside>
    </div>
  );
}
